import { useAuth } from "@/providers/auth";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { SyncConfigCard } from "@/components/dashboard/integrations/sync-config-card";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AliasForm } from "@/components/dashboard/integrations/shopify/alias-form";
import { shopifyAccountQueryOpts } from "@/hooks/shopify";
import { ProductFetchForm } from "@/components/dashboard/integrations/shopify/product-fetch-form";
import { RemoveConfirmation } from "@/components/dashboard/integrations/shopify/remove-account";
import { isGated } from "@/hooks/gated";
import { syncConfigQueryOpts } from "@/hooks/sync-config";
import { ebayAccountsQueryOpts } from "@/hooks/ebay";
import { getOrgIdWithFallback } from "@/lib/utils";
import GatedOverlay from "@/components/ui/gated-overlay";
import { useOrg } from "@/providers/organization";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute(
  "/$org/integrations/shopify/$alias/account",
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const { getToken, setToken, handleTokenInvalid } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);

    const gated = await isGated(
      {
        token: getToken() as string,
        setToken,
        orgId: orgId,
      },
      handleTokenInvalid,
    );

    const account = await context.queryClient.ensureQueryData(
      shopifyAccountQueryOpts(params.alias, {
        setToken,
        token: getToken() as string,
        orgId: orgId,
      }),
    );

    await context.queryClient.ensureQueryData(
      syncConfigQueryOpts(account.platform_connection_id, {
        setToken,
        token: getToken() as string,
        orgId: orgId,
      }),
    );

    await context.queryClient.ensureQueryData(
      ebayAccountsQueryOpts({
        setToken,
        token: getToken() as string,
        orgId: orgId,
      }),
    );

    return { gated: gated?.is_gated };
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const { gated } = Route.useLoaderData();
  const { org } = Route.useParams();
  const { alias } = Route.useParams();
  const { orgId } = useOrg();
  const resolvedOrgId = getOrgIdWithFallback(orgId);
  const { getToken, setToken } = useAuth();
  const { data: account } = useQuery(
    shopifyAccountQueryOpts(alias, {
      setToken,
      token: getToken() as string,
      orgId: resolvedOrgId,
    }),
  );
  const { data: ebayAccounts = [] } = useQuery(
    ebayAccountsQueryOpts({
      setToken,
      token: getToken() as string,
      orgId: resolvedOrgId,
    }),
  );

  if (gated) {
    return <GatedOverlay org={org} />;
  }

  return (
    <div>
      <div className="mx-auto max-w-3xl">
        {account && (
          <div className="space-y-2">
            <Card>
              <CardHeader>
                <CardTitle>{t("integrations.account.details")}</CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">
                <AliasForm account={account} />
              </CardContent>
              <CardFooter className="flex justify-end">
                <Button
                  form="alias-form"
                  name="intent"
                  value={"details"}
                  type="submit"
                >
                  {t("common.save")}
                </Button>
              </CardFooter>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>{t("integrations.account.fetchTitle")}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col text-sm font-semibold">
                <span>{t("integrations.account.fetchDescription")}</span>
                <span className="text-xs text-gray-600">
                  {t("integrations.account.fetchHint")}
                </span>
              </CardContent>
              <CardFooter className="flex justify-end">
                <ProductFetchForm account={account} />
              </CardFooter>
            </Card>
            <SyncConfigCard
              platformConnectionId={account.platform_connection_id}
              showEndListingWhenQuantityZero
              endListingPlatform="Shopify"
              showAutomatedExports
              exportTargetAccounts={ebayAccounts}
              exportTargetPlatform="eBay"
            />
            <div className="flex justify-end">
              <RemoveConfirmation alias={account.alias} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
